import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { notificationsAPI } from "@/lib/notifications-api";
import { getApiErrorStatus } from "@/lib/api";
import { queryKeys } from "@/lib/query-keys";
import { useAuthStore } from "@/stores/authStore";
import type {
  MarkAllReadParams,
  Nudge,
  NudgeListParams,
} from "@/types/notifications";

/**
 * Nudges inbox hooks.
 *
 * The backend is committed but not deployed, so these fail until it lands.
 * The page shows an honest error state rather than pretending to have data.
 */

const PAGE_SIZE = 20;

export const useNotificationsFeed = (params?: NudgeListParams) => {
  const { user } = useAuthStore();

  return useInfiniteQuery({
    queryKey: queryKeys.notifications.list({ ...params, page_size: PAGE_SIZE }),
    queryFn: ({ pageParam }) =>
      notificationsAPI.list({
        ...params,
        page: pageParam,
        page_size: PAGE_SIZE,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, pages) =>
      lastPage.next ? pages.length + 1 : undefined,
    enabled: !!user,
    retry: false,
  });
};

export const useNotificationsUnreadCount = () => {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: queryKeys.notifications.unreadCount(),
    queryFn: () => notificationsAPI.unreadCount(),
    enabled: !!user,
    retry: false,
  });
};

/**
 * The category registry, owned by the backend.
 *
 * Long staleTime because the taxonomy changes on deploys, not on minutes, and
 * this is read on every feed render. Failure is not an error state — the feed
 * still works, it just falls back to humanised keys and an unfiltered list.
 */
export const useNudgeCategories = () => {
  const { user } = useAuthStore();

  return useQuery({
    queryKey: queryKeys.notifications.categories(),
    queryFn: () => notificationsAPI.categories(),
    enabled: !!user,
    retry: false,
    staleTime: 60 * 60 * 1000,
  });
};

export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => notificationsAPI.markRead(id),

    // Opening a nudge should feel instant. The endpoint is idempotent, so
    // replaying this on a retry is safe.
    onMutate: async (id) => {
      await queryClient.cancelQueries({
        queryKey: queryKeys.notifications.all,
      });

      const previous = queryClient.getQueriesData({
        queryKey: queryKeys.notifications.all,
      });

      const readAt = new Date().toISOString();

      queryClient.setQueriesData(
        { queryKey: queryKeys.notifications.all },
        (old: any) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page: any) => ({
              ...page,
              results: page.results.map((n: Nudge) =>
                n.id === id && n.read_at === null
                  ? { ...n, read_at: readAt }
                  : n
              ),
            })),
          };
        }
      );

      return { previous };
    },

    onError: (error, _id, context) => {
      // 409 means this was a chat nudge, which clears only by opening the
      // conversation. Rolling back is the whole handling — the user is being
      // navigated into that conversation anyway, which is what clears it.
      context?.previous?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });

      if (getApiErrorStatus(error) !== 409) {
        // Anything else is a real failure worth refetching against.
        queryClient.invalidateQueries({
          queryKey: queryKeys.notifications.all,
        });
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
};

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (params?: MarkAllReadParams) =>
      notificationsAPI.markAllRead(params),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });
};
